import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const getHeaders = () => {
    const token = localStorage.getItem("token");
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

// Ödeme niyeti oluşturma
export const createPaymentIntent = createAsyncThunk(
    "payment/createPaymentIntent",
    async ({ availabilityId, therapistId, couponCode, sessionType }, { rejectWithValue }) => {
        try {
            const body = { availabilityId, therapistId };
            if (sessionType) body.sessionType = sessionType;
            if (couponCode) body.couponCode = couponCode;
            const response = await axios.post(
                `${API_URL}/payments/create-payment-intent`,
                body,
                { headers: getHeaders() }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error creating payment" });
        }
    }
);

// Ödeme durumunu kontrol etme
export const verifyPaymentStatus = createAsyncThunk(
    "payment/verifyPaymentStatus",
    async (paymentId, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${API_URL}/payments/verify/${paymentId}`,
                { headers: getHeaders() }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error checking payment status" });
        }
    }
);

// Kullanıcının ödemelerini getirme
export const getUserPayments = createAsyncThunk(
    "payment/getUserPayments",
    async ({ status, page = 1, limit = 10 } = {}, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            if (status) params.append('status', status);
            params.append('page', page.toString());
            params.append('limit', limit.toString());

            const response = await axios.get(
                `${API_URL}/payments/user?${params.toString()}`,
                { headers: getHeaders() }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error getting payments" });
        }
    }
);

// İade edilebilir ödemeleri getirme
export const getRefundablePayments = createAsyncThunk(
    "payment/getRefundablePayments",
    async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            params.append('page', page.toString());
            params.append('limit', limit.toString());

            const response = await axios.get(
                `${API_URL}/payments/refundable?${params.toString()}`,
                { headers: getHeaders() }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error getting refundable payments" });
        }
    }
);

// Ödeme iadesi yapma (availabilityId ile) - YENİ ESCROW SİSTEMİ
export const refundPayment = createAsyncThunk(
    "payment/refundPayment",
    async ({ availabilityId, reason }, { rejectWithValue }) => {
        try {
            // Yeni escrow sistemi endpoint'i
            const response = await axios.post(
                `${API_URL}/escrow/cancel/user/${availabilityId}`,
                { reason },
                { headers: getHeaders() }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error refunding payment" });
        }
    }
);

// Terapist tarafından iptal - YENİ ESCROW SİSTEMİ
export const cancelByTherapist = createAsyncThunk(
    "payment/cancelByTherapist",
    async ({ availabilityId, cancelReason, reason }, { rejectWithValue }) => {
        try {
            // cancelReason: 'customer_request' veya 'therapist_initiative'
            const response = await axios.post(
                `${API_URL}/escrow/cancel/therapist/${availabilityId}`,
                { cancelReason, reason },
                { headers: getHeaders() }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error canceling appointment" });
        }
    }
);

// Randevu erteleme (Reschedule) - YENİ ESCROW SİSTEMİ
export const rescheduleAppointment = createAsyncThunk(
    "payment/rescheduleAppointment",
    async ({ oldAvailabilityId, newAvailabilityId }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${API_URL}/escrow/reschedule`,
                { oldAvailabilityId, newAvailabilityId },
                { headers: getHeaders() }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error rescheduling appointment" });
        }
    }
);

// Escrow durumunu getir - YENİ ESCROW SİSTEMİ
export const getEscrowStatus = createAsyncThunk(
    "payment/getEscrowStatus",
    async (paymentId, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${API_URL}/escrow/status/${paymentId}`,
                { headers: getHeaders() }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error getting escrow status" });
        }
    }
);

// Tüm ödemeleri getirme (Admin only)
export const getAdminPayments = createAsyncThunk(
    "payment/getAdminPayments",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${API_URL}/payments`,
                { headers: getHeaders() }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error getting payments" });
        }
    }
);

const paymentSlice = createSlice({
    name: "payment",
    initialState: {
        paymentIntent: null,
        paymentStatus: null,
        userPayments: [],
        adminPayments: [],
        refundablePayments: [],
        refundResult: null,
        pagination: null,
        refundablePagination: null,
        loading: false,
        verifyLoading: false,
        userPaymentsLoading: false,
        adminPaymentsLoading: false,
        refundablePaymentsLoading: false,
        refundLoading: false,
        error: null,
        verifyError: null,
        userPaymentsError: null,
        adminPaymentsError: null,
        refundablePaymentsError: null,
        refundError: null
    },
    reducers: {
        clearPaymentError: (state) => {
            state.error = null;
            state.verifyError = null;
            state.userPaymentsError = null;
            state.refundablePaymentsError = null;
            state.refundError = null;
        },
        clearPaymentIntent: (state) => {
            state.paymentIntent = null;
        },
        clearPaymentStatus: (state) => {
            state.paymentStatus = null;
        },
        setPaymentIntent: (state, action) => {
            state.paymentIntent = action.payload;
        },
        clearRefundResult: (state) => {
            state.refundResult = null;
            state.refundError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Create Payment Intent
            .addCase(createPaymentIntent.pending, (state) => {
                state.loading = true;
                state.error = null;
            })  
            .addCase(createPaymentIntent.fulfilled, (state, action) => {
                state.loading = false;
                state.paymentIntent = action.payload;
                state.error = null;
            })
            .addCase(createPaymentIntent.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.paymentIntent = null;
            })
            
            // Verify Payment Status
            .addCase(verifyPaymentStatus.pending, (state) => {
                state.verifyLoading = true;
                state.verifyError = null;
            })
            .addCase(verifyPaymentStatus.fulfilled, (state, action) => {
                state.verifyLoading = false;
                state.paymentStatus = action.payload.data;
                state.verifyError = null;
            })
            .addCase(verifyPaymentStatus.rejected, (state, action) => {
                state.verifyLoading = false;
                state.verifyError = action.payload;
            })
            
            // Get User Payments
            .addCase(getUserPayments.pending, (state) => {
                state.userPaymentsLoading = true;
                state.userPaymentsError = null;
            })
            .addCase(getUserPayments.fulfilled, (state, action) => {
                state.userPaymentsLoading = false;
                state.userPayments = action.payload.data;
                state.pagination = action.payload.pagination;
                state.userPaymentsError = null;
            })
            .addCase(getUserPayments.rejected, (state, action) => {
                state.userPaymentsLoading = false;
                state.userPaymentsError = action.payload;
            })
            
            // Get Refundable Payments
            .addCase(getRefundablePayments.pending, (state) => {
                state.refundablePaymentsLoading = true;
                state.refundablePaymentsError = null;
            })
            .addCase(getRefundablePayments.fulfilled, (state, action) => {
                state.refundablePaymentsLoading = false;
                state.refundablePayments = action.payload.data;
                state.refundablePagination = action.payload.pagination;
                state.refundablePaymentsError = null;
            })
            .addCase(getRefundablePayments.rejected, (state, action) => {
                state.refundablePaymentsLoading = false;
                state.refundablePaymentsError = action.payload;
            })
            
            // Refund Payment
            .addCase(refundPayment.pending, (state) => {
                state.refundLoading = true;
                state.refundError = null;
                state.refundResult = null;
            })
            .addCase(refundPayment.fulfilled, (state, action) => {
                state.refundLoading = false;
                state.refundResult = action.payload;
                state.refundError = null;
                
                // İade edilen ödemeyi userPayments listesinden güncelle
                const refundedAvailabilityId = action.payload.data?.availabilityId;
                if (refundedAvailabilityId) {
                    state.userPayments = state.userPayments.map(payment => {
                        if (payment.details?.availabilityId === refundedAvailabilityId) {
                            return {
                                ...payment,
                                status: 'REFUNDED',
                                refundedAt: new Date().toISOString()
                            };
                        }
                        return payment;
                    });
                    
                    // İade edilebilir ödemeler listesinden kaldır
                    state.refundablePayments = state.refundablePayments.filter(
                        payment => payment.details?.availabilityId !== refundedAvailabilityId
                    );
                }
            })
            .addCase(refundPayment.rejected, (state, action) => {
                state.refundLoading = false;
                state.refundError = action.payload;
                state.refundResult = null;
            })
            // Get Admin Payments
            .addCase(getAdminPayments.pending, (state) => {
                state.adminPaymentsLoading = true;
                state.adminPaymentsError = null;
            })
            .addCase(getAdminPayments.fulfilled, (state, action) => {
                state.adminPaymentsLoading = false;
                state.adminPayments = action.payload?.data || action.payload || [];
            })
            .addCase(getAdminPayments.rejected, (state, action) => {
                state.adminPaymentsLoading = false;
                state.adminPaymentsError = action.payload;
            });
    },
});

export const { 
    clearPaymentError, 
    clearPaymentIntent, 
    clearPaymentStatus,
    setPaymentIntent,
    clearRefundResult
} = paymentSlice.actions;

export default paymentSlice.reducer;